import { useEffect, useState } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  DatePicker,
  Button,
  Space,
  Tabs,
  Spin,
  Tag,
  Progress,
  message,
  Modal,
  Checkbox,
  Input,
  Form,
} from 'antd'
import {
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  RiseOutlined,
  FallOutlined,
  ExportOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import { saveAs } from 'file-saver'
import { statisticsAPI, orderAPI } from '../../api'

const { Option } = Select
const { RangePicker } = DatePicker

interface SalesTrend {
  date: string
  sales: number
  orders: number
  customers: number
}

interface ProductRanking {
  id: number
  name: string
  spec: string
  salesCount: number
  salesAmount: number
  trend: 'up' | 'down' | 'stable'
  trendPercent: number
}

interface CustomerRanking {
  id: number
  shopName: string
  phone: string
  orderCount: number
  totalAmount: number
  avgAmount: number
  lastOrderDate: string
}

interface StoreRanking {
  id: number
  name: string
  orderCount: number
  totalAmount: number
  customerCount: number
}

const COLORS = ['#006835', '#1890ff', '#fa8c16', '#52c41a', '#722ed1', '#eb2f96']

// 订单状态映射
const orderStatusMap: Record<string, string> = {
  pending: '待付款',
  paid: '待发货',
  delivered: '已发货',
  completed: '已完成',
  cancelled: '已取消',
}

// 支付方式映射
const paymentMethodMap: Record<string, string> = {
  wechat: '微信支付',
  alipay: '支付宝',
  offline: '线下转账',
}

// 支付状态映射
const paymentStatusMap: Record<string, string> = {
  pending: '待审核',
  verified: '已审核',
  rejected: '已驳回',
}

// 可导出的字段配置
const exportFields = [
  { key: 'orderNumber', label: '订单号', default: true },
  { key: 'createdAt', label: '下单日期', default: true },
  { key: 'customerName', label: '客户名称', default: true },
  { key: 'customerPhone', label: '客户电话', default: true },
  { key: 'storeName', label: '所属门店', default: true },
  { key: 'totalAmount', label: '商品总额', default: true },
  { key: 'discountAmount', label: '优惠金额', default: true },
  { key: 'payAmount', label: '实付金额', default: true },
  { key: 'status', label: '订单状态', default: true },
  { key: 'paymentMethod', label: '支付方式', default: true },
  { key: 'paymentStatus', label: '支付状态', default: false },
  { key: 'deliveryDate', label: '配送日期', default: true },
  { key: 'deliveryAddress', label: '配送地址', default: true },
  { key: 'deliveryNote', label: '配送备注', default: false },
  { key: 'items', label: '商品明细', default: false },
  { key: 'cancelReason', label: '取消原因', default: false },
]

const Statistics = () => {
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ])
  const [trendRange, setTrendRange] = useState<'7days' | '30days' | '90days'>('30days')
  const [activeTab, setActiveTab] = useState('overview')

  // 统计数据
  const [overviewStats, setOverviewStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    totalCustomers: 0,
    avgOrderAmount: 0,
    salesGrowth: 0,
    ordersGrowth: 0,
    customersGrowth: 0,
    repurchaseRate: 0,
  })

  // 销售趋势
  const [salesTrend, setSalesTrend] = useState<SalesTrend[]>([])

  // 商品排行
  const [productRanking, setProductRanking] = useState<ProductRanking[]>([])
  const [productSortBy, setProductSortBy] = useState<'salesCount' | 'salesAmount'>('salesCount')

  // 客户排行
  const [customerRanking, setCustomerRanking] = useState<CustomerRanking[]>([])

  // 门店排行
  const [storeRanking, setStoreRanking] = useState<StoreRanking[]>([])

  // 分类销售占比
  const [categoryData, setCategoryData] = useState<{ name: string; value: number }[]>([])

  // 订单导出相关状态
  const [exportModalVisible, setExportModalVisible] = useState(false)
  const [exportDateRange, setExportDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ])
  const [exportLoading, setExportLoading] = useState(false)
  const [selectedFields, setSelectedFields] = useState<string[]>(
    exportFields.filter(f => f.default).map(f => f.key)
  )
  const [exportFileName, setExportFileName] = useState('订单导出')

  useEffect(() => {
    fetchAllData()
  }, [dateRange, trendRange])

  const fetchAllData = async () => {
    setLoading(true)
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD')
      const endDate = dateRange[1].format('YYYY-MM-DD')

      await Promise.all([
        fetchOverviewStats(startDate, endDate),
        fetchSalesTrend(startDate, endDate),
        fetchProductRanking(startDate, endDate),
        fetchCustomerRanking(startDate, endDate),
        fetchStoreRanking(startDate, endDate),
        fetchCategoryData(startDate, endDate),
      ])
    } catch (error) {
      // 错误由 request 拦截器统一处理
    } finally {
      setLoading(false)
    }
  }

  const fetchOverviewStats = async (startDate: string, endDate: string) => {
    try {
      const res = await statisticsAPI.getOverview({ startDate, endDate })
      if (res.code === 200) {
        setOverviewStats(res.data)
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  const fetchSalesTrend = async (startDate: string, endDate: string) => {
    try {
      const res = await statisticsAPI.getSalesTrend({ startDate, endDate, range: trendRange })
      if (res.code === 200) {
        setSalesTrend(res.data)
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  const fetchProductRanking = async (startDate: string, endDate: string) => {
    try {
      const res = await statisticsAPI.getProductRanking({ startDate, endDate, sortBy: productSortBy })
      if (res.code === 200) {
        setProductRanking(res.data)
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  const fetchCustomerRanking = async (startDate: string, endDate: string) => {
    try {
      const res = await statisticsAPI.getCustomerRanking({ startDate, endDate })
      if (res.code === 200) {
        setCustomerRanking(res.data)
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  const fetchStoreRanking = async (startDate: string, endDate: string) => {
    try {
      const res = await statisticsAPI.getStoreRanking({ startDate, endDate })
      if (res.code === 200) {
        setStoreRanking(res.data)
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  const fetchCategoryData = async (startDate: string, endDate: string) => {
    try {
      const res = await statisticsAPI.getCategoryRatio({ startDate, endDate })
      if (res.code === 200) {
        setCategoryData(res.data)
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  // 导出订单
  const handleExportOrders = async () => {
    if (selectedFields.length === 0) {
      message.warning('请至少选择一个导出字段')
      return
    }

    setExportLoading(true)
    try {
      const startDate = exportDateRange[0].format('YYYY-MM-DD')
      const endDate = exportDateRange[1].format('YYYY-MM-DD')

      const res = await orderAPI.export({
        startDate,
        endDate,
        fields: selectedFields,
      })

      // 处理文件下载
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const fileName = `${exportFileName}_${startDate}_至_${endDate}.xlsx`
      saveAs(blob, fileName)

      message.success('导出成功')
      setExportModalVisible(false)
    } catch (error) {
      message.error('导出失败')
    } finally {
      setExportLoading(false)
    }
  }

  // 全选/取消全选字段
  const handleSelectAllFields = (checked: boolean) => {
    if (checked) {
      setSelectedFields(exportFields.map(f => f.key))
    } else {
      setSelectedFields([])
    }
  }

  // 恢复默认字段
  const handleResetFields = () => {
    setSelectedFields(exportFields.filter(f => f.default).map(f => f.key))
  }

  const handleExport = () => {
    message.info('报表导出功能开发中')
  }

  const productColumns: ColumnsType<ProductRanking> = [
    { title: '排名', key: 'rank', width: 60, render: (_, __, index) => index + 1 },
    { title: '商品名称', dataIndex: 'name', key: 'name', width: 150 },
    { title: '规格', dataIndex: 'spec', key: 'spec', width: 100 },
    {
      title: '销量',
      dataIndex: 'salesCount',
      key: 'salesCount',
      width: 100,
      sorter: (a, b) => a.salesCount - b.salesCount,
    },
    {
      title: '销售额',
      dataIndex: 'salesAmount',
      key: 'salesAmount',
      width: 120,
      render: (val: number) => `¥${val.toFixed(2)}`,
      sorter: (a, b) => a.salesAmount - b.salesAmount,
    },
    {
      title: '趋势',
      dataIndex: 'trend',
      key: 'trend',
      width: 100,
      render: (val: string, record: ProductRanking) => (
        <span style={{ color: val === 'up' ? '#52c41a' : val === 'down' ? '#cf1322' : '#999' }}>
          {val === 'up' ? <RiseOutlined /> : val === 'down' ? <FallOutlined /> : '—'}
          {' '}{record.trendPercent > 0 ? '+' : ''}{record.trendPercent}%
        </span>
      ),
    },
  ]

  const customerColumns: ColumnsType<CustomerRanking> = [
    { title: '排名', key: 'rank', width: 60, render: (_, __, index) => index + 1 },
    { title: '店铺名称', dataIndex: 'shopName', key: 'shopName', width: 160 },
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 130 },
    {
      title: '订单数',
      dataIndex: 'orderCount',
      key: 'orderCount',
      width: 80,
      sorter: (a, b) => a.orderCount - b.orderCount,
    },
    {
      title: '消费总额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (val: number) => `¥${val.toFixed(2)}`,
      sorter: (a, b) => a.totalAmount - b.totalAmount,
    },
    {
      title: '客单价',
      dataIndex: 'avgAmount',
      key: 'avgAmount',
      width: 100,
      render: (val: number) => `¥${val.toFixed(2)}`,
    },
    { title: '最后下单', dataIndex: 'lastOrderDate', key: 'lastOrderDate', width: 120 },
  ]

  const storeColumns: ColumnsType<StoreRanking> = [
    { title: '排名', key: 'rank', width: 60, render: (_, __, index) => index + 1 },
    { title: '门店名称', dataIndex: 'name', key: 'name', width: 180 },
    {
      title: '订单数',
      dataIndex: 'orderCount',
      key: 'orderCount',
      width: 100,
      sorter: (a, b) => a.orderCount - b.orderCount,
    },
    {
      title: '销售额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 130,
      render: (val: number) => `¥${val.toFixed(2)}`,
      sorter: (a, b) => a.totalAmount - b.totalAmount,
    },
    {
      title: '客户数',
      dataIndex: 'customerCount',
      key: 'customerCount',
      width: 80,
    },
    {
      title: '客单价',
      key: 'avgPerCustomer',
      width: 100,
      render: (_, record) => `¥${(record.totalAmount / record.orderCount).toFixed(2)}`,
    },
  ]

  return (
    <div className="statistics-page">
      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="销售概览" key="overview">
            {/* 筛选栏 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space wrap>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                />
                <Select value={trendRange} onChange={setTrendRange} style={{ width: 120 }}>
                  <Option value="7days">近7天</Option>
                  <Option value="30days">近30天</Option>
                  <Option value="90days">近90天</Option>
                </Select>
                <Button type="primary" onClick={fetchAllData}>查询</Button>
                <Button icon={<ExportOutlined />} onClick={handleExport}>导出报表</Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => setExportModalVisible(true)}
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                  导出订单
                </Button>
              </Space>
            </Card>

            {/* 核心指标 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="总销售额"
                    value={overviewStats.totalSales}
                    precision={2}
                    prefix={<DollarOutlined />}
                    suffix={
                      <span style={{ fontSize: 14, marginLeft: 8 }}>
                        {overviewStats.salesGrowth > 0 ? <RiseOutlined style={{ color: '#52c41a' }} /> : <FallOutlined style={{ color: '#cf1322' }} />}
                        {overviewStats.salesGrowth > 0 ? '+' : ''}{overviewStats.salesGrowth}%
                      </span>
                    }
                    valueStyle={{ color: '#006835' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="总订单数"
                    value={overviewStats.totalOrders}
                    prefix={<ShoppingCartOutlined />}
                    suffix={
                      <span style={{ fontSize: 14, marginLeft: 8 }}>
                        {overviewStats.ordersGrowth > 0 ? <RiseOutlined style={{ color: '#52c41a' }} /> : <FallOutlined style={{ color: '#cf1322' }} />}
                        {overviewStats.ordersGrowth > 0 ? '+' : ''}{overviewStats.ordersGrowth}%
                      </span>
                    }
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="消费客户数"
                    value={overviewStats.totalCustomers}
                    prefix={<UserOutlined />}
                    suffix={
                      <span style={{ fontSize: 14, marginLeft: 8 }}>
                        {overviewStats.customersGrowth > 0 ? <RiseOutlined style={{ color: '#52c41a' }} /> : <FallOutlined style={{ color: '#cf1322' }} />}
                        {overviewStats.customersGrowth > 0 ? '+' : ''}{overviewStats.customersGrowth}%
                      </span>
                    }
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="客单价"
                    value={overviewStats.avgOrderAmount}
                    precision={2}
                    prefix="¥"
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* 复购率 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Card size="small" title="客户复购率">
                  <Row align="middle">
                    <Col span={4}>
                      <Statistic
                        title="复购率"
                        value={overviewStats.repurchaseRate}
                        precision={1}
                        suffix="%"
                        valueStyle={{ color: '#52c41a', fontSize: 32 }}
                      />
                    </Col>
                    <Col span={20}>
                      <Progress
                        percent={overviewStats.repurchaseRate}
                        strokeColor="#52c41a"
                        strokeWidth={20}
                        showInfo={false}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            {/* 销售趋势图 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Card size="small" title="销售趋势">
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={salesTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip
                        formatter={(value: any, name: any) => {
                          if (name === '销售额') return [`¥${Number(value).toLocaleString()}`, name]
                          return [Number(value).toLocaleString(), name]
                        }}
                      />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="sales"
                        name="销售额"
                        stroke="#cf1322"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="orders"
                        name="订单数"
                        stroke="#006835"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="customers"
                        name="客户数"
                        stroke="#1890ff"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>

            {/* 分类占比 + 门店排行 */}
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title="分类销售占比">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="门店销售排行 TOP5">
                  <Table
                    columns={storeColumns}
                    dataSource={storeRanking.slice(0, 5)}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          </Tabs.TabPane>

          <Tabs.TabPane tab="商品排行" key="products">
            <Card size="small">
              <Space style={{ marginBottom: 16 }}>
                <span>排序方式：</span>
                <Select value={productSortBy} onChange={setProductSortBy} style={{ width: 120 }}>
                  <Option value="salesCount">按销量</Option>
                  <Option value="salesAmount">按销售额</Option>
                </Select>
                <Button icon={<ExportOutlined />} onClick={handleExport}>导出</Button>
              </Space>
              <Table
                columns={productColumns}
                dataSource={productRanking}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </Tabs.TabPane>

          <Tabs.TabPane tab="客户排行" key="customers">
            <Card size="small">
              <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button icon={<ExportOutlined />} onClick={handleExport}>导出</Button>
              </div>
              <Table
                columns={customerColumns}
                dataSource={customerRanking}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </Tabs.TabPane>

          <Tabs.TabPane tab="门店排行" key="stores">
            <Card size="small">
              <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button icon={<ExportOutlined />} onClick={handleExport}>导出</Button>
              </div>
              <Table
                columns={storeColumns}
                dataSource={storeRanking}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </Tabs.TabPane>
        </Tabs>
      </Spin>

      {/* 订单导出弹窗 */}
      <Modal
        title="导出订单数据"
        open={exportModalVisible}
        onOk={handleExportOrders}
        onCancel={() => setExportModalVisible(false)}
        confirmLoading={exportLoading}
        width={600}
        okText="开始导出"
        cancelText="取消"
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="导出日期范围" required>
            <RangePicker
              value={exportDateRange}
              onChange={(dates) => setExportDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item label="文件名">
            <Input
              value={exportFileName}
              onChange={(e) => setExportFileName(e.target.value)}
              placeholder="请输入文件名"
              addonAfter=".xlsx"
            />
          </Form.Item>

          <Form.Item label="选择导出字段">
            <Space style={{ marginBottom: 8 }}>
              <Checkbox
                checked={selectedFields.length === exportFields.length}
                indeterminate={selectedFields.length > 0 && selectedFields.length < exportFields.length}
                onChange={(e) => handleSelectAllFields(e.target.checked)}
              >
                全选
              </Checkbox>
              <Button size="small" onClick={handleResetFields}>
                恢复默认
              </Button>
            </Space>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 16px' }}>
              {exportFields.map(field => (
                <Checkbox
                  key={field.key}
                  checked={selectedFields.includes(field.key)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedFields([...selectedFields, field.key])
                    } else {
                      setSelectedFields(selectedFields.filter(f => f !== field.key))
                    }
                  }}
                >
                  {field.label}
                </Checkbox>
              ))}
            </div>
          </Form.Item>

          <div style={{
            padding: '8px 12px',
            background: '#f5f5f5',
            borderRadius: 4,
            fontSize: 12,
            color: '#666'
          }}>
            提示：导出的订单数据将按照选择的字段生成 Excel 文件，预计包含所选日期范围内的所有订单。
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default Statistics
